import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useBrand } from '../context/BrandContext';
import { Shield, CreditCard, Lock, Check, ArrowRight, Loader2, Compass, Zap, Crown, Gem, Gift } from 'lucide-react';

import { UserProfile } from '../types';

export const MembershipCheckout: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, profile, updateProfile, isMember, signUp } = useUser();
  const { membershipPlans, addOrder } = useBrand();

  const planId = searchParams.get('planId');

  useEffect(() => {
    // If they are a member and NOT trying to upgrade, redirect to portal
    if (isMember && (!profile || profile.membershipTier !== 'Prospect' || planId === 'prospect-tier')) {
      navigate('/member-portal');
    }
  }, [isMember, navigate, profile, planId]);
  const [loading, setLoading] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [step, setStep] = useState(1); // 1: Review, 2: Payment, 3: Success
  const [acceptedWaiver, setAcceptedWaiver] = useState(false);
  const [formError, setFormError] = useState('');
  const [generatedPass, setGeneratedPass] = useState('');

  const [giftCode, setGiftCode] = useState('');
  const [giftCardApplied, setGiftCardApplied] = useState<{ code: string; amount: number; type: string } | null>(null);
  const [giftCardError, setGiftCardError] = useState('');
  const [giftCardLoading, setGiftCardLoading] = useState(false);

  const handleApplyGiftCard = async () => {
    if (!giftCode.trim()) return;
    setGiftCardError('');
    setGiftCardLoading(true);
    try {
      const response = await fetch('/api/validate-gift-card', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: giftCode.trim().toUpperCase(),
          bookingType: 'membership_purchase'
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Invalid gift card or promo code.');
      }

      const data = await response.json();
      setGiftCardApplied({
        code: giftCode.trim().toUpperCase(),
        amount: data.amount,
        type: data.type
      });
      setGiftCode('');
    } catch (err: any) {
      setGiftCardError(err.message || 'Verification failed.');
    } finally {
      setGiftCardLoading(false);
    }
  };

  const handleRemoveGiftCard = () => {
    setGiftCardApplied(null);
    setGiftCardError('');
  };

  const staticFallbackPlans = [
    {
      id: 'prospect-tier',
      name: 'Prospect',
      price: 0,
      interval: 'one-time',
      tier: 'Prospect',
      icon: 'Compass',
      features: [
        'Pay-As-You-Go for All Training',
        'Standard Rate for Expeditions',
        'Official OWS Portal Access',
        'Basic Performance Tracking'
      ]
    },
    {
      id: 'syndicate-ultimate',
      name: 'Syndicate Member',
      price: 300,
      interval: 'per season',
      tier: 'Syndicate',
      icon: 'Shield',
      features: [
        '50% Off Local Training & Coaching Sessions',
        'Official Syndicate Membership & Rank',
        'Instructor-Led Safety Sessions',
        'Endurance Progression Tracking'
      ]
    }
  ];

  const [guestInfo, setGuestInfo] = useState({ name: '', email: '', phone: '' });

  const plan = membershipPlans.find(p => p.id === planId) || staticFallbackPlans.find(p => p.id === planId);

  const basePrice = plan?.price || 0;
  const taxAmount = basePrice * 0.13;
  const originalTotal = basePrice + taxAmount;

  const discountAmount = giftCardApplied ? Math.min(originalTotal, giftCardApplied.amount) : 0;
  const finalTotalToPay = Math.max(0, originalTotal - discountAmount);

  useEffect(() => {
    if (!plan && membershipPlans.length > 0) {
      navigate('/syndicate'); // go back to syndicate instead of membership
    }
  }, [plan, navigate, membershipPlans]);

  const handleCompletePurchase = async () => {
    if (!plan) return;

    if (!user && plan.price !== 0) {
      if (!guestInfo.name || !guestInfo.email) {
        setFormError('Identity verification required: Name and Email must be provided.');
        return;
      }
    }

    if (!user && plan.price === 0) {
      if (!guestInfo.name || !guestInfo.email) {
        setFormError('Identity verification required: Name and Email must be provided.');
        return;
      }
    }

    if (!acceptedWaiver) {
      setFormError('You must accept the liability waiver to proceed.');
      return;
    }

    setLoading(true);
    try {
      if (giftCardApplied && discountAmount > 0) {
        // Redeem gift card balance (fully or partially)
        const redeemRes = await fetch('/api/redeem-gift-card', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code: giftCardApplied.code,
            userId: user?.uid || 'guest',
            name: profile?.displayName || user?.displayName || guestInfo.name,
            email: user?.email || guestInfo.email,
            phone: guestInfo.phone || 'N/A',
            serviceName: `Membership Setup: ${plan.name}`,
            priceToDeduct: discountAmount,
            bookingType: 'membership_purchase'
          })
        });

        if (!redeemRes.ok) {
          const errData = await redeemRes.json().catch(() => ({}));
          throw new Error(errData.error || 'Failed to apply and redeem your code balance.');
        }
      }

      if (plan.price > 0 && finalTotalToPay > 0) {
        // ... stripe init ...
        let response;
        let retries = 3;
        while (retries > 0) {
          try {
            response = await fetch('/api/create-checkout-session', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                planId: plan.id,
                planName: plan.name,
                price: Math.max(0, plan.price - (giftCardApplied ? giftCardApplied.amount : 0)),
                tax: finalTotalToPay * 0.13,
                userId: user?.uid || 'guest',
                email: user?.email || guestInfo.email,
                name: profile?.displayName || user?.displayName || guestInfo.name,
                waiverAccepted: acceptedWaiver,
                giftCardCode: giftCardApplied ? giftCardApplied.code : undefined,
                giftCardDiscount: discountAmount
              })
            });
            if (response.ok || (response.status >= 400 && response.status < 500 && response.status !== 404)) break;
          } catch (e) {
            // ignore network err in loop
          }
          retries--;
          if (retries > 0) await new Promise(res => setTimeout(res, 1500));
        }

        if (!response || !response.ok) {
          if (response && response.status === 404) {
            throw new Error("Protocol Failure 404: Endpoint unreachable. Please check if an ad-blocker or strict proxy is interfering.");
          }
          const errData = response ? await response.json().catch(() => ({})) : {};
          throw new Error(errData.error || `Protocol Failure: ${response?.status || 'Network Disconnected'}`);
        }

        const session = await response.json();
        if (session.url) {
          setCheckoutUrl(session.url);

          setTimeout(() => {
            window.location.href = session.url;
          }, 100);
          return;
        }
      }

      // If a guest requests a free Prospect tier OR pays fully with a Promo Code / Gift Card, trigger secure direct email verification or direct premium setup!
      if (!user && (plan.price === 0 || finalTotalToPay === 0)) {
        try {
          const response = await fetch('/api/register-prospect-verification', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: guestInfo.name,
              email: guestInfo.email,
              phone: guestInfo.phone,
              planId: plan.id
            })
          });

          if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            throw new Error(errData.error || 'Verification dispatch failed.');
          }

          const resData = await response.json();
          if (resData?.credentials?.password) {
            setGeneratedPass(resData.credentials.password);
          }

          setStep(3);
          return;
        } catch (authError: any) {
          setFormError(authError.message || 'An error occurred during verification dispatch.');
          setLoading(false);
          return;
        }
      }

      // Create a membership order in Firestore for free/fully paid plans (when logged in, or fallback)
      const orderData = {
        userId: user?.uid || 'guest',
        userEmail: user?.email || guestInfo.email,
        userName: profile?.displayName || user?.displayName || guestInfo.name,
        type: 'membership_purchase' as const,
        items: [{
          id: plan.id,
          name: plan.name,
          price: plan.price,
          quantity: 1,
          giftCodeUsed: giftCardApplied ? giftCardApplied.code : null,
          giftCardDiscount: discountAmount
        }],
        total: finalTotalToPay,
        tax: finalTotalToPay * 0.13,
        subtotal: Math.max(0, plan.price - (giftCardApplied ? giftCardApplied.amount : 0)),
        shippingAddress: {
          name: profile?.displayName || user?.displayName || guestInfo.name,
          street: 'Syndicate Digital Activation',
          city: 'Cloud',
          postalCode: '00000'
        }
      };

      await addOrder(orderData);

      // Update generic local profile state
      const profileUpdates = {
        membershipTier: plan.name as UserProfile['membershipTier'],
        membershipStatus: 'active' as const,
        membershipId: plan.id,
        role: 'member' as const,
        joinedAt: new Date().toISOString()
      };

      if (user) {
        await updateProfile(profileUpdates);
      }

      setStep(3);
    } catch (error: any) {
      console.error("Membership purchase failed", error);
      setFormError(error.message || "Membership purchase failed");
    } finally {
      setLoading(false);
    }
  };

  if (!plan) return null;

  const getTierIcon = (iconName: string) => {
    switch (iconName) {
      case 'Compass': return <Compass className="h-12 w-12 text-ows-teal" />;
      case 'Zap': return <Zap className="h-12 w-12 text-ows-teal" />;
      case 'Crown': return <Crown className="h-12 w-12 text-ows-teal" />;
      case 'Gem': return <Gem className="h-12 w-12 text-ows-teal" />;
      default: return <Shield className="h-12 w-12 text-ows-teal" />;
    }
  };

  return (
    <div className="min-h-screen bg-ows-sand pt-32 pb-20 px-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-12">
          <h1 className="text-4xl font-display font-light uppercase tracking-widest text-ows-dark">Syndicate <span className="text-ows-teal">Activation</span></h1>
          <div className="flex items-center gap-4">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`w-3 h-3 rounded-full transition-all duration-500 ${step >= s ? 'bg-ows-teal scale-125' : 'bg-ows-dark/10'}`}
              />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            {loading && !checkoutUrl && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-white p-12 rounded-[3.5rem] shadow-2xl border border-ows-teal/5 text-center"
              >
                <div className="w-24 h-24 bg-ows-teal/10 rounded-full flex items-center justify-center mx-auto mb-10">
                  <Loader2 className="h-12 w-12 text-ows-teal animate-spin" />
                </div>
                <h2 className="text-xl font-display font-light uppercase tracking-[0.3em] text-ows-dark">Establishing Secure Protocol</h2>
                <p className="mt-4 text-[10px] font-bold uppercase tracking-widest text-ows-dark/40">Encrypting data for mission terminal...</p>
              </motion.div>
            )}

            {checkoutUrl && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-white p-12 rounded-[3.5rem] shadow-2xl border border-ows-teal/5 text-center space-y-8"
              >
                <div className="w-24 h-24 bg-ows-teal/10 rounded-full flex items-center justify-center mx-auto animate-bounce">
                  <Shield className="h-12 w-12 text-ows-teal" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-xl font-display font-light uppercase tracking-[0.3em] text-ows-dark">Authorization Ready</h2>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/40 max-w-sm mx-auto leading-relaxed">
                    Identity verified. Secure gateway ready. If the terminal doesn't open automatically, use the button below.
                  </p>
                </div>

                <div className="flex flex-col gap-4 pt-4">
                  <button
                    onClick={() => {
                      window.location.href = checkoutUrl;
                    }}
                    className="px-12 py-6 bg-ows-teal text-white text-[10px] font-bold uppercase tracking-[0.4em] hover:bg-ows-dark transition-all rounded-2xl flex items-center justify-center gap-3 shadow-2xl shadow-ows-teal/20 mx-auto"
                  >
                    Open Secure Terminal <ArrowRight className="h-4 w-4" />
                  </button>

                  <a
                    href={checkoutUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[9px] text-ows-dark/30 uppercase tracking-widest hover:text-ows-teal transition-colors underline"
                  >
                    Open in New Tab (Bypass Iframe Blocks)
                  </a>
                </div>
              </motion.div>
            )}

            {!loading && step === 1 && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white p-10 rounded-[3rem] shadow-2xl border border-ows-teal/5"
              >
                <div className="flex items-center gap-6 mb-10">
                  <div className="w-20 h-20 bg-ows-sand rounded-3xl flex items-center justify-center">
                    {getTierIcon(plan.icon)}
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-ows-teal mb-1">Selected Tier</p>
                    <h2 className="text-3xl font-display font-light uppercase tracking-widest text-ows-dark">{plan.name}</h2>
                  </div>
                </div>

                <div className="space-y-6 mb-12">
                  <h3 className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/40">Tier Privileges</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {plan.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-3 text-xs tracking-widest uppercase text-ows-dark/60">
                        <Check className="h-4 w-4 text-ows-teal" />
                        {feature}
                      </div>
                    ))}
                  </div>

                  {plan.id === 'syndicate-ultimate' && (
                    <div className="mt-8 p-6 bg-ows-orange/5 border border-ows-orange/20 rounded-3xl">
                      <div className="flex items-center gap-3 mb-3 text-ows-orange">
                        <Shield className="h-5 w-5" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.2em]">Deployment Standard & Clearance Protocol</span>
                      </div>
                      <div className="space-y-3">
                        <p className="text-[10px] leading-relaxed text-ows-dark/60 font-light">
                          Notice: Initiation grants you immediate hardware rights and training access. However, active deployment on the **Lake Ontario Night Crossings** and **Baja Expedition** is strictly gated.
                        </p>
                        <p className="text-[10px] leading-relaxed text-ows-dark/60 font-medium">
                          Members must pass the OWS Deployment Standard (4.0 km/h velocity / 15km endurance) and receive **manual Admin approval** before mission windows can be reserved.
                        </p>
                        <p className="text-[10px] italic text-ows-dark/40 leading-relaxed">
                          *Most applicants do not yet qualify; this protocol does not prevent you from joining the Syndicate and training with the core team.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {!user && (
                  <div className="space-y-6 mb-12 p-8 bg-ows-sand/30 rounded-3xl border border-ows-dark/5">
                    <h3 className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/40">Identity Verification (Required)</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/30 ml-2">Full Name</label>
                        <input
                          type="text"
                          value={guestInfo.name}
                          onChange={(e) => setGuestInfo(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-6 py-4 rounded-xl bg-white border border-ows-teal/5 focus:border-ows-teal outline-none transition-all text-sm"
                          placeholder="Marcus Thorne"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/30 ml-2">Contact Phone</label>
                        <input
                          type="tel"
                          value={guestInfo.phone}
                          onChange={(e) => setGuestInfo(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full px-6 py-4 rounded-xl bg-white border border-ows-teal/5 focus:border-ows-teal outline-none transition-all text-sm"
                          placeholder="+1 (555) 000-0000"
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/30 ml-2">Email Address</label>
                        <input
                          type="email"
                          value={guestInfo.email}
                          onChange={(e) => setGuestInfo(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full px-6 py-4 rounded-xl bg-white border border-ows-teal/5 focus:border-ows-teal outline-none transition-all text-sm"
                          placeholder="marcus@explorer.com"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="border-t border-ows-teal/10 pt-10">
                  {plan.price > 0 && (
                    <div className="mb-8 p-6 bg-ows-sand/30 rounded-3xl border border-ows-dark/5 space-y-4">
                      <div>
                        <h4 className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/50 mb-1">Corporate & Promo Redemption</h4>
                        <p className="text-[9px] text-ows-dark/40 uppercase tracking-wider font-mono">Apply promotional codes or gift card values</p>
                      </div>

                      {giftCardApplied ? (
                        <div className="flex items-center justify-between p-4 bg-green-50 rounded-2xl border border-green-200">
                          <div className="flex items-center gap-3">
                            <Gift className="h-5 w-5 text-green-600" />
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-widest text-green-800 font-mono">Applied: {giftCardApplied.code}</p>
                              <p className="text-[9px] text-green-600 uppercase tracking-wider font-mono">Value: ${discountAmount.toFixed(2)} (Balance: ${giftCardApplied.amount})</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={handleRemoveGiftCard}
                            className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 text-[9px] font-bold uppercase tracking-widest rounded-xl transition-all font-mono"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex gap-3">
                            <input
                              type="text"
                              value={giftCode}
                              onChange={(e) => {
                                setGiftCode(e.target.value);
                                setGiftCardError('');
                              }}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApplyGiftCard(); } }}
                              placeholder="ENTER PROMO OR GIFT VOUCHER CODE"
                              className="flex-1 px-5 py-4 border border-ows-teal/5 bg-white rounded-2xl text-[10px] uppercase font-mono tracking-widest outline-none transition-all placeholder:text-ows-dark/20 text-ows-dark focus:border-ows-teal"
                            />
                            <button
                              type="button"
                              onClick={handleApplyGiftCard}
                              disabled={giftCardLoading || !giftCode.trim()}
                              className="px-6 bg-ows-dark text-white text-[9px] font-bold uppercase tracking-widest rounded-2xl hover:bg-ows-teal min-w-[100px] disabled:opacity-50 transition-all flex items-center justify-center font-mono"
                            >
                              {giftCardLoading ? <Loader2 className="h-4 w-4 animate-spin text-white" /> : 'Apply'}
                            </button>
                          </div>
                          {giftCardError && (
                            <p className="text-[9px] text-red-500 uppercase tracking-widest font-mono ml-2">{giftCardError}</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mb-8 p-6 bg-ows-sand/30 rounded-2xl border border-ows-dark/5">
                    <div className="flex items-start gap-4">
                      <input
                        type="checkbox"
                        id="checkout-waiver"
                        required
                        checked={acceptedWaiver}
                        onChange={(e) => {
                          setAcceptedWaiver(e.target.checked);
                          if (e.target.checked) setFormError('');
                        }}
                        className="mt-0.5 w-5 h-5 accent-ows-orange rounded cursor-pointer shrink-0"
                      />
                      <label htmlFor="checkout-waiver" className="text-[10px] uppercase tracking-widest text-ows-dark/70 leading-relaxed cursor-pointer select-none">
                        I voluntarily assume all risks, including serious injury or death, associated with OWS activities and equipment. I completely release, hold harmless, and indemnify OWS, its agents, and partners from any and all liability, claims, or damages, whether caused by negligence or otherwise. <span className="text-ows-teal font-bold block mt-2 text-[11px]">Check to accept Full Liability Waiver & Release</span>
                      </label>
                    </div>
                  </div>

                  {formError && (
                    <p className="text-[10px] text-red-500 uppercase tracking-widest text-center mb-6">{formError}</p>
                  )}

                  <button
                    onClick={handleCompletePurchase}
                    disabled={loading}
                    className="w-full py-6 bg-ows-dark text-white text-[10px] font-bold uppercase tracking-[0.4em] rounded-2xl hover:bg-ows-teal transition-all flex items-center justify-center gap-4 group shadow-xl"
                    id="ows-authorize-membership"
                  >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                      (plan.price === 0 || finalTotalToPay === 0) ? "Request Activation" : `Initiate Secure Payment — $${finalTotalToPay.toFixed(2)}`
                    )}
                    {(plan.price !== 0 && finalTotalToPay > 0) && <ArrowRight className="h-4 w-4 group-hover:translate-x-2 transition-transform" />}
                  </button>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white p-12 rounded-[3rem] shadow-2xl border border-ows-teal/5 text-center"
              >
                {plan.price === 0 || finalTotalToPay === 0 && !user ? (
                  <>
                    <div className="w-24 h-24 bg-ows-teal/10 rounded-full flex items-center justify-center mx-auto mb-10">
                      <Shield className="h-12 w-12 text-ows-teal" />
                    </div>
                    <h2 className="text-4xl font-display font-light uppercase tracking-widest text-ows-dark mb-6">Entry <span className="text-ows-teal">Granted</span></h2>

                    <div className="mb-10 p-8 bg-ows-dark text-white rounded-3xl border border-ows-teal/20 mx-auto text-left max-w-md">
                      <h3 className="text-xs font-bold uppercase tracking-widest text-ows-teal mb-4 flex items-center gap-2">
                        <Zap className="h-4 w-4" /> Welcome to The Syndicate
                      </h3>
                      <p className="text-xs leading-relaxed text-white/80 font-mono tracking-tight mb-4 border-b border-white/10 pb-3">
                        We have successfully registered your free **Prospect Membership** profile. Your secure system credentials and passkey have been dispatched to <strong className="text-ows-teal font-sans font-bold">{guestInfo.email}</strong>.
                      </p>

                      <div className="mt-4 p-4 bg-white/5 border border-white/10 rounded-2xl">
                        <p className="text-[9px] font-bold uppercase tracking-widest text-white/40 mb-2">Instant Access Credentials:</p>
                        <div className="space-y-1.5 text-xs text-left">
                          <p className="font-mono text-white/70"><span className="text-[10px] text-white/30 inline-block w-20 font-sans font-bold">EMAIL ID:</span>{guestInfo.email}</p>
                          <p className="font-mono text-white/70"><span className="text-[10px] text-white/30 inline-block w-20 font-sans font-bold">PASSKEY:</span><strong className="text-ows-teal tracking-wider text-sm select-all">{generatedPass || 'Generating...'}</strong></p>
                        </div>
                      </div>

                      <button
                        onClick={() => navigate('/login')}
                        className="mt-6 block w-full py-4 bg-ows-teal/20 hover:bg-ows-teal/30 text-ows-teal font-bold text-[10px] uppercase tracking-widest text-center rounded-xl border border-ows-teal/40 transition-colors"
                      >
                        Proceed To Portal Login
                      </button>
                    </div>

                    <div className="space-y-8">
                      <p className="text-xs text-ows-dark/60 italic font-serif mb-12 max-w-sm mx-auto">
                        "Your credentials have been securely registered. Copy your passkey above and enter the command center."
                      </p>
                      <button
                        onClick={() => navigate('/login')}
                        className="px-12 py-6 bg-ows-dark text-white text-[10px] font-bold uppercase tracking-[0.4em] rounded-2xl hover:bg-ows-teal transition-all flex items-center justify-center gap-4 mx-auto group shadow-xl"
                      >
                        Enter Member Portal <ArrowRight className="h-4 w-4 group-hover:translate-x-2 transition-transform" />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-24 h-24 bg-ows-teal/10 rounded-full flex items-center justify-center mx-auto mb-10" id="membership-success-badge">
                      <Shield className="h-12 w-12 text-ows-teal" />
                    </div>
                    <h2 className="text-4xl font-display font-light uppercase tracking-widest text-ows-dark mb-6">Welcome to the <span className="text-ows-teal">Syndicate</span></h2>

                    <div className="mb-10 p-6 bg-ows-dark text-white rounded-3xl border border-ows-teal/20 mx-auto text-left max-w-md">
                      <h3 className="text-xs font-bold uppercase tracking-widest text-ows-teal mb-4 flex items-center gap-2">
                        <Zap className="h-4 w-4" /> Credentials Activated
                      </h3>
                      <p className="text-sm font-light text-white/80 mb-4 italic">
                        "Your digital liability waiver is signed and filed. You now have full clearance for deployment. Access your credentials in the portal."
                      </p>

                      {plan.price === 0 || finalTotalToPay === 0 && (
                        <div className="mb-6 p-4 bg-white/5 border border-white/10 rounded-xl">
                          <p className="text-[10px] font-mono text-white/60 leading-relaxed">
                            A secure link has been dispatched to your email. Please check your inbox to set your permanent portal password.
                          </p>
                        </div>
                      )}

                      <Link to="/member-portal" className="block w-full py-4 bg-ows-teal/20 text-ows-teal font-bold text-[10px] uppercase tracking-widest text-center rounded-xl border border-ows-teal/40 hover:bg-ows-teal/30 transition-colors">
                        Continue to Portal
                      </Link>
                    </div>

                    <div className="space-y-8">
                      <p className="text-sm text-ows-dark/60 italic font-serif mb-12 max-w-md mx-auto">
                        "Your credentials have been verified. The horizon is now yours to command."
                      </p>
                      <button
                        onClick={() => navigate('/member-portal')}
                        className="px-12 py-6 bg-ows-dark text-white text-[10px] font-bold uppercase tracking-[0.4em] rounded-2xl hover:bg-ows-teal transition-all flex items-center justify-center gap-4 mx-auto group shadow-xl"
                        id="membership-goto-portal"
                      >
                        Enter Member Portal <ArrowRight className="h-4 w-4 group-hover:translate-x-2 transition-transform" />
                      </button>
                    </div>
                  </>
                )}
              </motion.div>
            )}
          </div>

          <div className="space-y-8">
            <div className="bg-ows-dark p-8 rounded-[2.5rem] text-white shadow-2xl">
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-ows-teal mb-6">Order Summary</h3>
              <div className="space-y-4 mb-8">
                <div className="flex justify-between items-center">
                  <span className="text-xs uppercase tracking-widest text-white/40">{plan.name} Tier</span>
                  <span className="text-sm font-bold">${plan.price}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs uppercase tracking-widest text-white/40">Activation Fee</span>
                  <span className="text-sm font-bold text-green-400">WAIVED</span>
                </div>
                {giftCardApplied && (
                  <div className="flex justify-between items-center">
                    <span className="text-xs uppercase tracking-widest text-green-400">Voucher Credit ({giftCardApplied.code})</span>
                    <span className="text-sm font-bold text-green-400">-${discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="pt-4 border-t border-white/10 flex justify-between items-center">
                  <span className="text-xs uppercase tracking-widest font-bold">Total Due</span>
                  <span className="text-xl font-display font-light text-ows-teal">${finalTotalToPay.toFixed(2)}</span>
                </div>
              </div>
              <p className="text-[8px] font-bold uppercase tracking-widest text-white/20 leading-relaxed mb-4">
                *Includes 13% HST (${(plan.price * 0.13).toFixed(2)})
              </p>
              <p className="text-[8px] font-bold uppercase tracking-widest text-white/20 leading-relaxed">
                {plan.interval === 'one-time'
                  ? 'One-time secure initiation. Lifetime legacy status upon successful qualification.'
                  : `Recurring billing every ${plan.interval || 'month'}. Cancel anytime via the Member Portal.`
                }
              </p>
            </div>

            <div className="bg-white p-8 rounded-[2.5rem] border border-ows-teal/5 shadow-xl">
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-ows-dark/40 mb-6">Member Benefits</h3>
              <div className="space-y-4">
                {[
                  'Priority Batch Access',
                  'Live Satellite Tracking',
                  'Classified Intelligence Feed',
                  'Syndicate Hardware Discounts'
                ].map((benefit, i) => (
                  <div key={i} className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest text-ows-dark/60">
                    <div className="w-1.5 h-1.5 bg-ows-teal rounded-full" />
                    {benefit}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
